import fs from 'node:fs/promises'
import path from 'node:path'
import { text } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'

import { normalize } from '@scalar/openapi-parser'
import {
  createServerWorkspaceStore,
  WORKSPACE_FILE_NAME,
} from '@scalar/workspace-store/server'

import { getFileOrUrl } from '@/domains/document/helpers'
import { output } from '@/helpers/std-out'
import { measureTime } from '@/helpers/time'

export function SplitCommand() {
  const cmd = new Command('split')

  cmd.description('Split your OpenAPI documents on small chunks')
  cmd.argument('[file|url]', 'Path to OpenAPI file or URL to split')
  cmd.option('-o, --output <path>', 'Path to save the chunks')
  cmd.action(async (input: string, { output: __outputPath }) => {
    if (!input) {
      return output
        .error()
        .title('Missing input')
        .message('Please provide a file path or URL to split')
        .exit('error')
    }

    const getOutputPath = async () => {
      if (__outputPath !== undefined && typeof __outputPath === 'string') {
        return __outputPath
      }

      const outputPath = await text({
        message: 'Where should the chunks be written?',
        defaultValue: './assets',
        placeholder: './assets',
      })

      if (typeof outputPath !== 'string') {
        return output
          .error()
          .message('Operation cancelled by user')
          .exit('error')
      }

      return outputPath
    }

    const outputPath = await getOutputPath()
    const fileContents = await getFileOrUrl(input)

    const documentName = 'default'

    const store = await createServerWorkspaceStore({
      mode: 'static',
      documents: [
        {
          name: documentName,
          // Todo: remove the type casting
          document: normalize(fileContents) as Record<string, unknown>,
        },
      ],
      directory: outputPath,
    })

    // Execute the split operation and measure the time taken
    const { executionTimeMs } = await measureTime(async () => {
      // generate workspace chunks
      await store.generateWorkspaceChunks()

      const workspaceFilePath = path.join(outputPath, WORKSPACE_FILE_NAME)

      const sparseDocument = JSON.parse(
        await fs.readFile(workspaceFilePath, { encoding: 'utf-8' }),
      )

      // Write the sparse document
      await fs.writeFile(
        path.join(outputPath, 'out.json'),
        JSON.stringify(sparseDocument.documents[documentName]),
      )

      // delete workspace file
      await fs.rm(workspaceFilePath)
    })

    output
      .info()
      .line()
      .title(as.green('Document Split Successfully! 🎉'))
      .line()
      .message(`✨ Chunks have been generated at: ${as.blue(outputPath)}`)
      .line(`⏱️ Split completed in ${as.yellow(executionTimeMs.toFixed(2))}ms`)
      .line('')
      .print()
  })

  return cmd
}
